import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Toaster from "../utils/Toaster";
import { Box } from "@mui/material";
import { AppTypography } from "../common/Typography";
import Card from "../common/Card";
import { EmailInput, NameInput, PhoneInput } from "../common/Input";
import OTPInput from "../common/OTPInput";
import { Container } from "../common/Container";
import AppButton from "../common/Button";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../theme/ThemeContext";
import { useStoreInfo } from "../../context/StoreInfoContext";
import {
  useRegisterMutation,
  useSendOTPMutation,
  useVerifyOTPMutation,
} from "../../store/apiSlice";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";

const Register = () => {
  const theme = useTheme();
  const [step, setStep] = useState("form");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = usePreserveStoreIdNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { customerId, storeId } = useStoreInfo();
  const [register, { isLoading: registering }] = useRegisterMutation();
  const [sendOTP, { isLoading: sendingOTP }] = useSendOTPMutation();
  const [verifyOTP, { isLoading: verifyingOTP }] = useVerifyOTPMutation();

  const handleChange = (evt) => {
    const name = evt.target.name;
    const value =
      evt.target.type === "checkbox" ? evt.target.checked : evt.target.value;
    setFormData({ ...formData, [name]: value });
  };

  const handleOTPChange = (value) => {
    setOtp(value);
  };

  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const validateForm = () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phone
    ) {
      Toaster.show("Please fill in all required fields", "warning");
      return false;
    }

    if (!formData.email.includes("@")) {
      Toaster.show("Please enter a valid email address", "warning");
      return false;
    }

    if (formData.phone.length < 5) {
      Toaster.show("Please enter a valid phone number", "warning");
      return false;
    }

    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const result = await register({ customerId, storeId, ...formData }).unwrap();
      setStep("otp");
      startResendTimer();
      Toaster.show(result.message || "OTP sent to your email!", "success");
    } catch (error) {
      Toaster.show(
        error.data?.message || error.message || "Failed to send OTP",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      Toaster.show("Please enter the 6-digit OTP", "warning");
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOTP({ customerId, storeId, email: formData.email, otp }).unwrap();

      // Create user data and login
      const userData = {
        email: formData.email,
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        id: result.result?.user?.id || Date.now(),
      };

      // Check for token in different possible locations
      const token =
        result.result?.token ||
        result.token ||
        result.result?.access_token ||
        result.access_token;

      login(userData, token);
      Toaster.show(
        "Registration successful!",
        "success"
      );

      // Redirect to the page they were trying to access, or home page
      const from = location.state?.from?.pathname || "/";
      setTimeout(() => navigate(from, { replace: true }), 1200);
    } catch (error) {
      Toaster.show(
        error.data?.message || error.message || "Invalid OTP",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    try {
      const result = await sendOTP({ customerId, storeId, email: formData.email }).unwrap();
      startResendTimer();
      Toaster.show(result.message || "OTP resent successfully!", "success");
    } catch (error) {
      Toaster.show(
        error.data?.message || error.message || "Failed to resend OTP",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setStep("form");
    setOtp("");
    setResendTimer(0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...theme?.background,
        flex: 1,
        mb: 3,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Logo/Brand Section */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
            }}
          >
            <AppTypography
              variant="h3"
              style={theme?.typography?.heading}
              fontWeight={700}
              fontSize="2rem"
              textAlign="center"
            >
              {step === "form" ? "Create Account" : "Verify Email"}
            </AppTypography>
            <AppTypography
              variant="body1"
              style={theme?.typography?.body}
              fontSize="1rem"
              textAlign="center"
              sx={{ opacity: 0.9 }}
            >
              {step === "form"
                ? `Join ${theme?.company_name} Store`
                : `We've sent a 6-digit code to ${formData.email}`}
            </AppTypography>
          </Box>

          {/* Register Card */}
          <Card
            variant="elevated"
            sx={{
              width: "100%",
              maxWidth: 600,
              p: 4,
              borderRadius: 2,
              ...theme?.layout_styles?.paper,
            }}
          >
            {step === "form" ? (
              <Box
                component="form"
                onSubmit={handleRegister}
                autoComplete="on"
                noValidate
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                {/* Name Fields */}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: 250 }}>
                    <NameInput
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      size="large"
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 250 }}>
                    <NameInput
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      size="large"
                    />
                  </Box>
                </Box>

                {/* Email Input */}
                <EmailInput
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  size="large"
                />

                {/* Phone Input */}
                <PhoneInput
                  name="phone"
                  placeholder="+9198XX54XXX0 (Include country code)"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  size="large"
                />

                <AppButton
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    ...theme?.button_styles?.app,
                  }}
                >
                  {loading || registering ? "Sending OTP..." : "Send OTP"}
                </AppButton>
              </Box>
            ) : (
              // OTP Step
              <Box
                component="form"
                onSubmit={handleVerifyOTP}
                autoComplete="off"
                noValidate
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                {/* OTP Input */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <AppTypography
                    variant="body2"
                    style={theme?.typography?.body}
                    fontSize="0.875rem"
                    textAlign="center"
                    sx={{ opacity: 0.7 }}
                  >
                    Enter the 6-digit code sent to your email
                  </AppTypography>
                  <OTPInput
                    value={otp}
                    onChange={handleOTPChange}
                    disabled={loading}
                  />
                </Box>

                {/* Verify OTP Button */}
                <AppButton
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || verifyingOTP || otp.length !== 6}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    ...theme?.button_styles?.app,
                  }}
                >
                  {loading || verifyingOTP ? "Verifying..." : "Verify OTP"}
                </AppButton>

                {/* Resend OTP */}
                <Box sx={{ textAlign: "center" }}>
                  <AppTypography
                    variant="body2"
                    style={theme?.typography?.body}
                    fontSize="0.875rem"
                    textAlign="center"
                    sx={{ opacity: 0.7 }}
                  >
                    Didn't receive the code?{" "}
                    <AppTypography
                      component="button"
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendTimer > 0}
                      style={theme?.typography?.link}
                      fontSize="0.875rem"
                      fontWeight={600}
                      sx={{
                        textDecoration: "none",
                        cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                        border: "none",
                        background: "none",
                        "&:hover": {
                          textDecoration:
                            resendTimer > 0 ? "none" : "underline",
                        },
                        opacity: resendTimer > 0 ? 0.5 : 1,
                      }}
                    >
                      {resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : "Resend OTP"}
                    </AppTypography>
                  </AppTypography>
                </Box>

                {/* Back to Form */}
                <Box sx={{ textAlign: "center" }}>
                  <AppTypography
                    component="button"
                    type="button"
                    onClick={handleBackToForm}
                    style={theme?.typography?.link}
                    fontSize="0.875rem"
                    fontWeight={500}
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      border: "none",
                      background: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    ← Edit sign up details
                  </AppTypography>
                </Box>
              </Box>
            )}
          </Card>

          {/* Login Option */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <AppTypography
              variant="body2"
              style={theme?.typography?.body}
              fontSize="0.875rem"
              textAlign="center"
              sx={{ opacity: 0.7 }}
            >
              Already have an account?{" "}
              <AppTypography
                component={RouterLink}
                to={`/login${location.search || ""}`}
                style={theme?.typography?.link}
                fontSize="0.875rem"
                fontWeight={600}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Sign in here
              </AppTypography>
            </AppTypography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
